import FOCUSABLE_ELEMENT_SELECTORS from '../../../hooks/useFocusTrap';
import { KeyCodes } from '../../../utils/keyCodes';
import useScrollMobile from '../../../utils/useScrollMobile';
import { useScrollLock } from '../../../utils/useLockScroll';
import defaultClasses from './Modal.module.scss';
import ModalCloseButton from './ModalCloseButton';
import { ModalContext } from './ModalContext';
import clsx from 'clsx';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createBreakpoint, useClickAway } from 'react-use';
import mergeClasses from '../../../utils/shallowMerge';

const useBreakpoint = createBreakpoint({ Lm: 640 });

const Modal = forwardRef(
  (
    {
      size = 'normal',
      scrollingElementRef,
      children,
      onClose,
      autoFocus = true,
      fixedFooter = false,
      isMobileFullPage = false,
      hasCloseButton = true,
      disableAnimation = false,
      lockScrolling = true,
      startModalWidth,
      isMobilePaddings,
      mobilePaddings,
      sizeCloseButton,
      variantCloseButton,
      typeCloseButton,
      shapeCloseButton,
      fillCloseButton,
      widthCloseButton,
      heightCloseButton,
      buttonBlockTop,
      buttonBlockRight,
      buttonBlockWidth,
      buttonBlockHeight,
      mobileButtonBlockTop,
      mobileButtonBlockRight,
      mobileButtonBlockWidth,
      mobileButtonBlockHeight,
      className,
      customClasses,
      customClassesModal,
      wrapperStyles,
      openMobileKeyboard,
    },
    ref,
  ) => {
    const [loaded, setLoaded] = useState(false);
    const [hasModalTitle, setHasModalTitle] = useState(false);
    const [hasModalSection, setHasModalSection] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [focusTriggered, setFocusTriggered] = useState(false);
    const [firstFocusableEl, setFirstFocusableEl] = useState(null);
    const [lastFocusableEl, setLastFocusableEl] = useState(null);

    const styles = mergeClasses(defaultClasses, customClassesModal);

    const modalContent = useRef(null);
    const modalBody = useRef(null);
    const modalID = 'modal_id';
    const scrollingElement = useRef(null);

    const scrolledContainer = useRef(null);
    const childRef = useRef(null);

    const breakpoint = useBreakpoint();
    const isLargeMobile = breakpoint === 'Lm';

    const setScrollingElementRefs = useCallback(
      (node) => {
        scrollingElement.current = node;

        if (scrollingElementRef) {
          if (typeof scrollingElementRef === 'function') {
            scrollingElementRef(node);
          } else {
            // eslint-disable-next-line no-param-reassign
            scrollingElementRef.current = node;
          }
        }
      },
      [scrollingElementRef],
    );

    useScrollLock(lockScrolling);

    const modalContentRef = useCallback(
      (node) => {
        modalContent.current = node;

        if (!isLargeMobile) setScrollingElementRefs(node);
      },
      [isLargeMobile, setScrollingElementRefs],
    );

    const modalBodyRef = useCallback(
      (node) => {
        modalBody.current = node;

        if (isLargeMobile) setScrollingElementRefs(node);
      },
      [isLargeMobile, setScrollingElementRefs],
    );

    const setFirstFocus = () => {
      if (modalBody.current && autoFocus) {
        modalBody.current.focus();
      }
    };

    const manageFocus = () => {
      if (!focusTriggered || !modalContent.current) return;

      const focusableElements = modalContent.current.querySelectorAll(
        FOCUSABLE_ELEMENT_SELECTORS,
      );

      if (focusableElements.length > 0) {
        setFirstFocusableEl(focusableElements[0]);
        setLastFocusableEl(focusableElements[focusableElements.length - 1]);
      }
    };

    const keyboardHandler = (event) => {
      if (event.keyCode !== KeyCodes.tab) return;

      if (!focusTriggered) {
        setFocusTriggered(true);
        manageFocus();
      }

      if (
        event.shiftKey &&
        (document.activeElement === firstFocusableEl ||
          document.activeElement === modalBody.current)
      ) {
        event.preventDefault();

        lastFocusableEl?.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastFocusableEl
      ) {
        event.preventDefault();

        firstFocusableEl?.focus();
      }
    };

    const handleKeyDown = (event) => {
      if (onClose && event.key === 'Escape') {
        event.stopPropagation();
        onClose(event);
      }

      keyboardHandler(event);
    };

    const handleClickOutside = (event) => {
      onClose(event);
    };

    const callContextFunctions = () => {
      manageFocus();
    };

    const getScrollPosition = () => {
      if (scrollingElement.current) {
        return scrollingElement.current.scrollTop;
      }
      return null;
    };

    const setScrollPosition = (value) => {
      if (scrollingElement.current) {
        scrollingElement.current.scrollTop = value;
      }
    };

    useImperativeHandle(ref, () => ({
      getScrollPosition,
      setScrollPosition,
      modalBody,
      modalContent,
    }));

    // eslint-disable-next-line consistent-return
    useEffect(() => {
      if (disableAnimation) {
        setFirstFocus();
      } else {
        const timer = setTimeout(() => {
          setLoaded(true);
          setFirstFocus();
        }, 15);

        return () => {
          clearTimeout(timer);
        };
      }
      // the Modal can only transition in on mount
    }, []);

    useClickAway(modalContent, (e) => handleClickOutside(e));

    useScrollMobile(scrolledContainer);

    const hasCloseContainer = hasModalTitle || (onClose && hasCloseButton);

    return (
      <div
        className={clsx(
          styles.modalBody,
          isMobilePaddings
            ? styles.withMobilePaddings
            : styles.withoutMobilePaddings,
        )}
        style={{
          '--mobile-paddings': mobilePaddings && `${mobilePaddings}px`,
        }}
        tabIndex="0"
        onKeyDown={handleKeyDown}
        ref={modalBodyRef}
        role="dialog"
        autoFocus={autoFocus}
        aria-modal="true"
        aria-labelledby={modalID}
      >
        <div
          className={clsx(
            styles.modalWrapper,
            styles[size],
            {
              [` ${styles.noAnimation}`]: disableAnimation,
              [` ${styles.mobileFull}`]: isMobileFullPage,
              [` ${styles.fixedFooter}`]: fixedFooter,
              [` ${styles.loaded}`]: loaded,
              [` ${styles.openMobileKeyboard}`]: openMobileKeyboard,
            },
            className,
          )}
          id={modalID}
          style={{
            '--modal-width': startModalWidth && `${startModalWidth}px`,
          }}
        >
          <div
            className={clsx(styles.modalWrapperContent, styles[size], {
              [` ${styles.fixedFooter}`]: fixedFooter,
              [` ${styles.mobileFull}`]: isMobileFullPage,
            })}
            ref={modalContentRef}
            style={wrapperStyles}
          >
            <div className={styles.scrolledContainer} ref={scrolledContainer}>
              {hasCloseContainer && (
                <div
                  id={`modalHeader__${modalID}`}
                  style={{
                    '--close-container-top':
                      buttonBlockTop && `${buttonBlockTop}px`,
                    '--close-container-right':
                      buttonBlockRight && `${buttonBlockRight}px`,
                    '--close-container-height':
                      buttonBlockWidth && `${buttonBlockWidth}px`,
                    '--close-container-width':
                      buttonBlockHeight && `${buttonBlockHeight}px`,
                    '--close-container-mobile-top':
                      mobileButtonBlockTop && `${mobileButtonBlockTop}px`,
                    '--close-container-mobile-right':
                      mobileButtonBlockRight && `${mobileButtonBlockRight}px`,
                    '--close-container-mobile-height':
                      mobileButtonBlockWidth && `${mobileButtonBlockWidth}px`,
                    '--close-container-mobile-width':
                      mobileButtonBlockHeight && `${mobileButtonBlockHeight}px`,
                  }}
                  className={clsx(styles.closeContainer, styles[size], {
                    [` ${styles.mobileFull}`]: isMobileFullPage,
                  })}
                >
                  {onClose && hasCloseButton && (
                    <ModalCloseButton
                      customClasses={customClasses}
                      size={sizeCloseButton}
                      variant={variantCloseButton}
                      type={typeCloseButton}
                      shape={shapeCloseButton}
                      fill={fillCloseButton}
                      width={widthCloseButton}
                      height={heightCloseButton}
                      onClick={onClose}
                    />
                  )}
                </div>
              )}

              <ModalContext.Provider
                // eslint-disable-next-line react/jsx-no-constructed-context-values
                value={{
                  setHasModalTitle,
                  setHasModalSection: () => setHasModalSection(true),
                  removeHasModalSection: () => setHasModalSection(false),
                  callContextFunctions,
                  hasModalSection,
                  isMobileFullPage,
                  closable: !!onClose,
                  isInsideModal: true,
                }}
              >
                <div ref={childRef}>{children}</div>
                <div id={`modalFooter__${modalID}`} />
              </ModalContext.Provider>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default Modal;
